import clsx from "clsx";
import { FC, ReactNode } from "react";

import { BillingAccountChip } from "@/components/Portal/Documents/BillingAccounts/BillingAccountChip";
import {
  getBillingAccountName,
  getBillingAccountNumber,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { DrAccount } from "@/services/model";

type SummaryVariant = "default" | "inverted";

const variantMapping = {
  default: {
    chipTone: "default",
    name: "text-foreground-primary",
    meta: "text-foreground-tertiary",
  },
  inverted: {
    chipTone: "inverted",
    name: "text-foreground-primary-inverse",
    meta: "text-foreground-tertiary-inverse",
  },
} as const satisfies Record<SummaryVariant, object>;

interface Props {
  account: DrAccount;
  /** Shown at the end of the name row, e.g. the subscription count. */
  aside?: ReactNode;
  variant?: SummaryVariant;
}

/**
 * Number, name, reference and email of a billing account in one block.
 * Spans only: it sits inside buttons (picker option, accordion header), which
 * allow phrasing content. Every element is a flex item, so it renders the same.
 */
export const BillingAccountSummary: FC<Props> = ({
  account,
  aside,
  variant = "default",
}) => {
  const variantClasses = variantMapping[variant];

  return (
    <span className="flex min-w-0 flex-1 flex-col gap-2">
      <span className="flex flex-col items-start gap-2 tablet:flex-row tablet:items-center tablet:gap-4">
        <span className="flex w-full min-w-0 items-center gap-2 tablet:w-auto tablet:flex-1">
          {account.billingAccount && (
            <BillingAccountChip
              text={getBillingAccountNumber(account)}
              tone={variantClasses.chipTone}
            />
          )}
          <span
            className={clsx(
              "truncate text-body-base font-semibold leading-md",
              variantClasses.name,
            )}
          >
            {getBillingAccountName(account)}
          </span>
        </span>
        {aside}
      </span>
      <span
        className={clsx(
          "flex min-w-0 flex-col gap-1 text-body-sm leading-md tablet:flex-row tablet:gap-3",
          variantClasses.meta,
        )}
      >
        {account.billingReference && (
          <span className="truncate">{account.billingReference}</span>
        )}
        {account.billingEmail && (
          <span className="truncate">{account.billingEmail}</span>
        )}
      </span>
    </span>
  );
};
